import { css } from '@emotion/react';
import { Journey } from '@flysas-tech/book-value-stream-models';
import { ScreenReaderOnly, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { ReactElement } from 'react';
import { FlightSegment, isDefined } from 'shared';

import { getDurationString, mapSegementDetails } from '../../../utils';

import { FlightTime, GrayBox, InfoRow, SegmentFlightInfo } from './SegmentDetailsComponents';

const styles = {
  wrapper: css`
    padding: 0;
    margin: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    li {
      list-style: none;
    }
  `,
  container: css`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: ${tokens.dimension['space-4']};
  `,
  icon: css`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
  `,
  blueLine: css`
    border-left: 2px solid ${tokens.color.blue['40']};
  `,
  greyLine: css`
    border-left: 2px solid ${tokens.color.turquoise['80']};
  `,
  dash: css`
    position: absolute;
    left: -4px;
    width: 6px;
    height: 3px;
    background-color: ${tokens.color.blue['50']};
  `,
  startDash: css`
    top: 0;
  `,
  lastDash: css`
    bottom: 0;
  `,
  dot: css`
    position: absolute;
    left: -5px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 2px solid ${tokens.color.blue['50']};
    background-color: ${tokens.color.white};
  `,
  startDot: css`
    top: -2px;
  `,
  endDot: css`
    bottom: -2px;
  `,
  infoContainer: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-1']};
  `,
  infoRow: css`
    display: flex;
    flex-direction: column;

    @media (min-width: 768px) {
      flex-direction: row;
    }
  `,
  infoItem: css`
    position: relative;

    @media (min-width: 768px) {
      padding: 0 12px;

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        height: 16px;
        width: 1px;
        background-color: #ccc;
        top: 50%;
        transform: translateY(-50%);
      }

      :first-of-type {
        padding-left: 0;
      }
    }
  `,
};

export function SegmentDetails({ segments: flightSegments }: { segments: Journey['segments'] | FlightSegment[] }) {
  const segments = mapSegementDetails(flightSegments);
  return <SegmentDetailsUI segments={segments} />;
}

export type SegmentDetailsUIProps = ReturnType<typeof mapSegementDetails>;
export function SegmentDetailsUI({ segments }: { segments: SegmentDetailsUIProps }) {
  const rowElement = segments.reduce((acc: ReactElement[], segment, index) => {
    const isLast = index === segments.length - 1;
    const isFirst = index === 0;
    const showLayover = !isLast && segments.length > 1;

    const key = `${segment.departureDateTime.toFormat('HH:mm')}-${segment.departureDetails.airportName}`;
    const layover = showLayover ? (
      <div key={`${key}-layover`} css={[styles.container, styles.greyLine]}>
        <div css={[styles.dash, styles.startDash]} />

        <ScreenReaderOnly>
          {`${t('common:timeBetweenFlights')} ${getDurationString(
            segment.arrivelDateTime,
            segments[index + 1].departureDateTime,
            { long: true }
          )}`}
        </ScreenReaderOnly>
        <GrayBox>
          <div aria-hidden="true">
            {t('common:timeBetweenFlights')}{' '}
            {getDurationString(segment.arrivelDateTime, segments[index + 1].departureDateTime)}
          </div>
        </GrayBox>

        <div css={[styles.dash, styles.lastDash]} />
      </div>
    ) : undefined;

    const row = [
      <li key={`${key}-departure`}>
        <div css={[styles.container, styles.blueLine]}>
          {isFirst && <div css={[styles.dot, styles.startDot]} />}
          <ScreenReaderOnly>
            {t('a11y.departureDetails', {
              defaultValue: 'Departs at {{time}} from {{airport}}, {{city}}.',
              time: segment.departureDateTime.toFormat('HH:mm'),
              airport: segment.departureDetails.airportName,
              city: segment.departureDetails.city,
            })}
          </ScreenReaderOnly>
          <ScreenReaderOnly>
            {t('a11y.durationDetails', {
              defaultValue: 'Duration {{duration}}. Flight {{flight}}, {{aircraft}}. Operated by {{operatedBy}}.',
              duration: getDurationString(segment.departureDateTime, segment.arrivelDateTime, { long: true }),
              flight: segment.flightDetails.flight,
              aircraft: segment.flightDetails.aircraft,
              operatedBy: segment.flightDetails.operatedBy,
            })}
          </ScreenReaderOnly>
          <div aria-hidden="true">
            <FlightTime dateTime={segment.departureDateTime} />
            <InfoRow
              info={[
                segment.departureDetails.airportName,
                `${segment.departureDetails.city} ${segment.departureDetails.airportCode}`,
                segment.departureDetails.terminal
                  ? `${t('common:departureTerminal', { terminal: segment.departureDetails.terminal })}`
                  : undefined,
              ].filter(isDefined)}
            />
            <SegmentFlightInfo
              info={[
                `${t('common:flight')} ${segment.flightDetails.flight}`,
                segment.flightDetails.aircraft,
                `${t('operatedBy', { airline: segment.flightDetails.operatedBy })}`,
              ]}
              duration={getDurationString(segment.departureDateTime, segment.arrivelDateTime)}
            />
          </div>
        </div>
      </li>,
      <li key={`${key}-arrival`}>
        <div css={[styles.container, styles.blueLine]}>
          {isLast && <div css={[styles.dot, styles.endDot]} />}
          <ScreenReaderOnly>
            {t('a11y.arrivalDetails', {
              defaultValue: 'Arrives at {{time}} to {{airport}}, {{city}}.',
              time: segment.arrivelDateTime.toFormat('HH:mm'),
              airport: segment.arrivalDetails.airportName,
              city: segment.arrivalDetails.city,
            })}
          </ScreenReaderOnly>
          <div aria-hidden="true">
            <FlightTime dateTime={segment.arrivelDateTime} />
            <InfoRow
              info={[
                segment.arrivalDetails.airportName,
                `${segment.arrivalDetails.city} ${segment.arrivalDetails.airportCode}`,
                segment.arrivalDetails.terminal
                  ? `${t('common:arrivalTerminal', { terminal: segment.arrivalDetails.terminal })}`
                  : undefined,
              ].filter(isDefined)}
            />
          </div>
        </div>
        {layover}
      </li>,
    ];

    return [...acc, ...row];
  }, []);

  return <ol css={styles.wrapper}>{rowElement}</ol>;
}
